import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import { formatISODuration, intervalToDuration } from 'date-fns';

import PlayIcon from '~/ui/assets/icons/play.svg';
import { Button, ButtonVariant } from '~/ui/components/button';
import { PlayPauseButton } from '~/ui/components/button/playPause';
import { FullBleedCTAScope, FullBleedCTATrigger } from '~/ui/components/fullBleedCta';

import styles from './preview.module.scss';

export interface VideoPreviewProps {
  /** this is intentionally required but allowed to be undefined so it has to be provided */
  videoSrc: string | undefined;
  fullVideoRef: React.RefObject<HTMLVideoElement | null>;
  onClick?: () => void;
  isAmbient?: boolean | null;
}

export function VideoPreview({
  videoSrc,
  fullVideoRef,
  onClick,
  isAmbient = false,
}: VideoPreviewProps) {
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState<number>();

  useEffect(() => {
    const fullVideo = fullVideoRef.current;
    if (!fullVideo) {
      return;
    }

    const onLoad = () => setDuration(fullVideo.duration);
    fullVideo.addEventListener('loadedmetadata', onLoad);
    return () => fullVideo.removeEventListener('loadedmetadata', onLoad);
  }, [fullVideoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (paused) {
      video.pause();
    } else {
      video.play();
    }
  }, [paused]);

  return (
    <FullBleedCTAScope>
      <div
        aria-label={isAmbient ? undefined : 'Watch video'}
        className={cx(styles.preview, isAmbient && styles.isAmbient)}
        onClick={isAmbient ? undefined : onClick}
      >
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          className={styles.video}
          disablePictureInPicture
          disableRemotePlayback
          aria-hidden
          ref={videoRef}
        />
        <div className={styles.gradient} />
        {!isAmbient && (
          <FullBleedCTATrigger>
            <Button invert onClick={onClick} className={styles.playButton}>
              <PlayIcon /> Watch video
              {!!duration && (
                <>
                  {' '}
                  <time dateTime={formatAccessibleDuration(duration)}>
                    {formatDuration(duration)}
                  </time>
                </>
              )}
            </Button>
          </FullBleedCTATrigger>
        )}
        <PlayPauseButton
          onClick={event => {
            setPaused(prev => !prev);
            event.stopPropagation();
          }}
          isPaused={paused}
          variant={ButtonVariant.PRIMARY_TRANSPARENT}
          ariaLabels={{ paused: 'Play preview', playing: 'Pause preview' }}
          className={styles.pauseButton}
        />
      </div>
    </FullBleedCTAScope>
  );
}

function formatAccessibleDuration(duration?: number) {
  if (!duration) {
    return;
  }

  return formatISODuration(parseDuration(duration));
}

/**
 * @param duration duration in seconds
 */
function parseDuration(duration: number) {
  const parsedDuration = intervalToDuration({
    start: 0,
    end: duration * 1000,
  });
  parsedDuration.seconds = parsedDuration.seconds ? Math.round(parsedDuration.seconds) : undefined;
  return parsedDuration;
}

/**
 * @param duration duration in seconds
 */
function formatDuration(duration?: number) {
  if (!duration) {
    return '';
  }

  const { hours = 0, minutes = 0, seconds = 0 } = parseDuration(duration);
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  } else {
    return `${formattedMinutes}:${formattedSeconds}`;
  }
}
